import { useState } from 'react';
import { MessageCircle } from 'lucide-react';

import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface ConversationHeaderProps {
  progress: number;
  title?: string;
  logoUrl?: string;
  showProgress?: boolean;
}

/**
 * Header component for the conversational form view.
 * Displays logo/icon, title, and animated progress bar.
 */
export function ConversationHeader({
  progress,
  title,
  logoUrl,
  showProgress = true,
}: ConversationHeaderProps) {
  const [logoError, setLogoError] = useState(false);
  const showLogo = logoUrl && !logoError;

  return (
    <header className="sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto max-w-lg px-4 py-3">
        {/* Top row: Logo/Icon + Title */}
        <div className="mb-2 flex items-center justify-center gap-3">
          {showLogo ? (
            <img
              src={logoUrl}
              alt={title ? `${title} logo` : 'Form logo'}
              className="h-8 max-w-[100px] object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="flex size-8 items-center justify-center rounded-full bg-primary/10">
              <MessageCircle className="size-4 text-primary" />
            </div>
          )}

          {title && (
            <h1 className="text-base font-medium tracking-tight text-foreground">
              {title}
            </h1>
          )}
        </div>

        {/* Progress bar */}
        {showProgress && (
          <div className="flex items-center gap-3">
            <Progress
              value={progress}
              className={cn(
                'h-1.5 flex-1',
                'transition-all duration-500 ease-out'
              )}
            />
            <span
              className={cn(
                'text-xs font-medium tabular-nums',
                'min-w-[3ch] text-right',
                progress === 100 ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              {progress}%
            </span>
          </div>
        )}
      </div>
    </header>
  );
}
